import { romeApi, utils } from "framework";

const state = {
    ready: false,
    employerId: null,
    cobraPlans: [],
    cobraBundles: [],

    cobraPlan: {
        planId: null,
        carrier: null,
        policyNumber: null,
        planName: null,
        rateType: null,
        ageDeterminedBy: null,
        insuranceType: null,
        insuranceTypeOther: null,
        locationId: null,
        insuredType: null,
        canBundle: null,
        planType: null,
        fsaRenewalMonth: null,
        disabilityFee: null,
        benefitTerminationType: null,
        conversionToIndividualPlansAllowed: null,
    },
    locations: [],
    rateTypes: [
        { name: "Composite", id: "COMPOSITE", path: "CobraComposite" },
        { name: "Flat", id: "PLANFLATRATE", path: "CobraFlat" },
        { name: "Member Specific", id: "NORATE", path: "" },
        { name: "Age/Gender & Age of Children", id: "AGESEXMFWITHAGEOFCHILDREN", path: "CobraAgeGender",
            coverageLevel: false,
            individualTypes: ["MALE","FEMALE", "CHILDBYAGE", "UNDISCLOSED"] },
        { name: "Age/Gender & Number of Children", id: "AGESEXMFWITHNUMBEROFCHILDREN", path: "CobraAgeGender",
            coverageLevel: false,
            individualTypes: ["MALE","FEMALE", "NUMBEROFCHILDREN", "UNDISCLOSED"] },
        { name: "Age of Employee/Spouse & Number of Children", id: "AGEEMPLOYEESPOUSENUMBEROFCHILDREN", path: "CobraAgeGender",
            coverageLevel: false,
            individualTypes: ["EMPLOYEE","SPOUSE","NUMBEROFCHILDREN"] },
        { name: "Age of Employee with Coverage Levels", id: "AGEEMPCOVERAGELEVEL", path: "CobraAgeGender",
            coverageLevel: true,
            individualTypes: ["EMPLOYEE"] },
        { name: "Age/Gender of Employee with Coverage Levels", id: "AGESEXEMPLOYEECOVERAGELEVEL", path: "CobraAgeGender",
            coverageLevel: true,
            individualTypes: ["MALE","FEMALE", "UNDISCLOSED"] },
    ],
    coverageLevels: [
        { name: "Employee Only", id: "EE" },
        { name: "Employee + Spouse", id: "EE+SPOUSE" },
        { name: "Employee + Domestic Partner", id: "EE+DOMESTICPARTNER"},
        { name: "Employee + Child", id: "EE+CHILD" },
        { name: "Employee + Children", id: "EE+CHILDREN" },
        { name: "Employee + Family", id: "EE+FAMILY" },
        { name: "Employee + 1 dependent", id: "EE+1" },
        { name: "Employee + 2 dependents", id: "EE+2" },
        { name: "Spouse Only", id: "SPOUSEONLY" },
        { name: "Spouse + Child", id: "SPOUSE+CHILD" },
        { name: "Spouse + Children", id: "SPOUSE+CHILDREN" },
        { name: "Child Only", id: "CHILDONLY" }
    ],
    insuranceTypes: [
        { name: "Medical", id: "MEDICAL" },
        { name: "Dental", id: "DENTAL" },
        { name: "Vision", id: "VISION" },
        { name: "HCRA", id: "HCRA" },
        { name: "FSA", id: "FSA" },
        { name: "GAP", id: "GAP" },
        { name: "EAP", id: "EAP" },
        { name: "Pharmacy", id: "PHARMACY" },
        { name: "Life Insurance (units based)", id: "LIFE" },
        { name: "Life Insurance (non-units based)", id: "NULIFE" },
        { name: "Medical Savings Account", id: "MSA" },
        { name: "Health Savings Account", id: "HSA" },
        { name: "Other Non-Units Based", id: "NUOTHER1" },
        { name: "Other Non-Units Based 2", id: "NUOTHER2" },
        { name: "Cancer", id: "CANCER" },
        { name: "Medical  Expense Reimbursement Plan", id: "MERP" },
        { name: "Critical Illness", id: "CRITICALILLNESS" },
        { name: "Voluntary - Other", id: "VOLUNTARYOTHER" },
        { name: "Accidental Death and Dismemberment", id: "AD&D" },
        { name: "Chiropractic", id: "CHIROPRACTIC" },
        { name: "Dependent Life 1 (units based)", id: "DEPLIFE1" },
        { name: "Dependent Life 2 (units based)", id: "DEPLIFE2" },
        { name: "Dependent Life 3 (units based)", id: "DEPLIFE3" },
        { name: "Group Life (units based)", id: "GRPLIFE" },
        { name: "Group Life (non-units based)", id: "NUGRPLIFE" },
        { name: "Dependent Life 1 (non-units based)", id: "NUDEPLIFE1" },
        { name: "Dependent Life 2 (non-units based)", id: "NUDEPLIFE2" },
        { name: "Dependent Life 3 (non-units based)", id: "NUDEPLIFE3" },
        { name: "Accident - Unit Based", id: "ACCIDENTUNITBASED" },
        { name: "Accident - Non Unit Based", id: "ACCIDENTNONUNITBASED" },
        { name: "Other Units Based 1", id: "UOTHER1" },
        { name: "Other Units Based 2", id: "UOTHER2" },
        { name: "Other Units Based 3", id: "UOTHER3" },
        { name: "Voluntary Life (units based)", id: "VOLLIFE" },
        { name: "Voluntary Life (non-units based)", id: "NUVOLLIFE" },
        { name: "LTD - Non Unit Based", id: "LTDNONUNITBASED" },
        { name: "STD - Non Unit Based", id: "STDNONUNITBASED" },
        { name: "Custom Billing", id: "CUSTOMBILLING" }
    ],
};

const getters = {
    getPlan: (state) => (id) => {
        return state.cobraPlans.find(x => id == x.planId) || null;
    },
    getRateType: (state) => (id) => {
        return state.rateTypes.find(x => id == x.id) || null;
    },
    getCoverageLevel: (state) => (id) => {
        return state.coverageLevels.find(x => id == x.id) || null;
    },
    getInsuranceType: (state) => (id) => {
        return state.insuranceTypes.find(x => id == x.id) || null;
    },
    contacts: (state) => {
        return state.cobraPlans
            .filter(x => x.carrier && x.carrier.Contact && x.carrier.Contact.FirstName && x.carrier.Contact.LastName)
            .map(x => {
                return {
                    name: `${x.carrier.Contact.FirstName} ${x.carrier.Contact.LastName} - ${x.carrier.Contact.Email || x.carrier.Contact.Fax}`,
                    ...x.carrier.Contact,
                }
            })
            .sort((a,b) => a.name.localeCompare(b.name))
            .filter((elem, indx, arry) => indx ? arry[indx - 1].name !== elem.name : true)
    },
};

const actions = {
    async load(context, data) {
        if(!data.force && (!data.employerId || context.state.employerId == data.employerId)) return;
        // console.log("x-cobra-plans load data: ", data.employerId, state.employerId);
        context.dispatch("loadBundles", { employerId: data.employerId });
        context.dispatch("cobraConfig/load", { employerId: data.employerId }, {root:true});

        context.state.ready = false;
        let plans = await romeApi.get(`employer/${data.employerId}/cobra/plan`);
        let locations = await romeApi.get(`employer/${data.employerId}/location`);
        plans.forEach(x => {
            if(typeof x.carrier == "string") x.carrier = JSON.parse(x.carrier)
        })

        context.commit('setCobraLocations', locations);
        context.commit('setCobraPlans', plans);
        context.commit('setEmployerId', data.employerId);
        context.state.ready = true;
    },
    async save(context, data) {
        context.state.ready = false;
        let plan = Object.assign({}, context.state.cobraPlan);
        utils.copyCommonProperties(data.plan, plan);
        plan.carrier = JSON.stringify(data.plan.carrier);
        // console.log("x-cobra save plan: ", plan);
        let employerId = context.state.employerId || data.plan.employerId;
        let planId = await romeApi.post(`employer/${employerId}/cobra/plan`, plan);
        await context.dispatch("load", { employerId: employerId, force: true });
        context.state.ready = true;
        return planId;
    },
    async delete(context, data) {
        context.state.ready = false;
        await romeApi.delete(`employer/${context.state.employerId}/cobra/plan/${data.planId}`);
        context.dispatch("load", { employerId: context.state.employerId, force: true });
        context.state.ready = true;
        return data.planId;
    },
    async loadBundles(context, data) {
        // console.log("x-cobra loadBundles bundle: ", data);
        let bundles = await romeApi.get(`employer/${data.employerId}/cobra/bundle`);
        context.commit('setCobraBundles', bundles);
    },
    async saveBundle(context, data) {
        context.state.ready = false;
        // console.log("x-cobra saveBundle bundle: ", data);
        let bundleId = await romeApi.post(`employer/${context.state.employerId}/cobra/bundle`, data);
        context.dispatch("loadBundles", { employerId: context.state.employerId });
        context.state.ready = true;
        return bundleId;
    },
};

const mutations = {
    setEmployerId(state, employerId) {
        // console.log("x-cobra mutation setEmployer data: ", employerId);
        state.employerId = employerId;
    },
    setCobraPlans(state, data) {
        // console.log("x-cobra mutation setCobraPlan data: ", data);
        state.cobraPlans = data;
    },
    setCobraBundles(state, data) {
        // console.log("x-cobra mutation setCobraBundles data: ", data);
        state.cobraBundles = data;
    },
    setCobraLocations(state, data) {
        // console.log("x-cobra mutation setCobraLocations data: ", data);
        let divs = data.map(x => { return { id: x.locationId, name: x.name, isDefault: x.isDefault } });
        divs.push({ id: 0, name: 'All Divisions', isDefault: false });
        state.locations = divs;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/cobra/x-cobra-plans.js